import { Box, Button, Center, Flex, FormControl, FormErrorMessage, FormLabel, Heading, HStack, Image, Input, Radio, RadioGroup, Stack, Text, useColorModeValue, VStack } from '@chakra-ui/react';
import { useFormik } from 'formik';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PhoneInput, { parsePhoneNumber } from 'react-phone-number-input';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ModalLoading from '../../components/Loader/ModalLoader.jsx';
import { isEmptyObject } from '../../helper/utils/isEmptyObject.js';
import { urlNavigate } from '../../helper/utils/navigation.js';
import getStaticAsset from '../../helper/utils/utilsStaticAsset';
import { data as cityOptions } from '../../mock/city.json';
import { doHandleState } from '../../redux/waiver/waiver.actions.js';
import { WaiverRegistration } from './hooks/validation.js';

export default function FormUser() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const { t } = useTranslation();

    const { data = {}, loadingSubmission = false } = useSelector((s) => s?.waiver);
    const [step, setStep] = useState(0);

    const isEdit = params.get('isEdit') || false;
    const pages = JSON.parse(localStorage.getItem('page') || '{}');
    const returnUrl = params.get('return-url') || urlNavigate('online-sales', 'home');

    const formik = useFormik({
        initialValues: {
            fullname: '',
            birthDate: '',
            email: '',
            phone: '',
            gender: '',
            country: { value: 'ID', label: 'Indonesia' },
            city: { value: '', label: '' },
            address: '',
        },
        onSubmit: (values) => {
            dispatch(
                doHandleState('data', {
                    ...values,
                    country: values?.country?.value ?? '',
                    city: values?.city?.value,
                    dialCode: `+${parsePhoneNumber(values?.phone ?? '', 'ID')?.countryCallingCode ?? ''}`,
                    phoneNo: `0${parsePhoneNumber(values?.phone ?? '', 'ID')?.nationalNumber ?? ''}`,
                }),
            );

            localStorage.setItem('email', values.email);
            navigate(urlNavigate('waiver', 'add-children'));
        },
        enableReinitialize: true,
        validationSchema: WaiverRegistration,
    });

    const handleBack = () => {
        navigate(returnUrl);
        dispatch(doHandleState('email', ''));
        dispatch(doHandleState('data', {}));
        dispatch(doHandleState('minor', []));
    };

    const handleChangePhone = (e = '') => {
        formik.setFieldValue('phone', e);
    };

    const filterColors = (inputValue) => {
        return cityOptions.filter((i) => i.label.toLowerCase().includes(inputValue.toLowerCase()));
    };

    // Calculate max date (17 years ago)
    let today = new Date();
    let eighteenYearsAgo = new Date();
    eighteenYearsAgo.setFullYear(today.getFullYear() - 17);
    let dateMax = eighteenYearsAgo.toISOString().split('T')[0];

    useEffect(() => {
        if (isEdit) {
            formik.setValues({
                fullname: data?.fullname ?? '',
                email: data?.email ?? '',
                birthDate: data?.birthDate ?? '',
                phone: data?.phone ?? '',
                gender: data?.gender ?? '',
                country: data?.country ?? { value: 'ID', label: 'Indonesia' },
                city: data?.city,
                address: data?.address ?? '',
            });
        } else {
            if (!isEmptyObject(data)) {
                formik.setValues(data);
            } else {
                formik.handleReset();
            }
        }
    }, [isEdit]);

    useEffect(() => {
        setStep(0);
    }, []);

    return (
        <Box
            minH='100vh'
            w='full'
            bgImage={getStaticAsset((pages['home'] || {})['image'] || '')}
            bgSize='cover'
            bgPosition='center'
            bgRepeat='no-repeat'
            position='relative'
            _before={{
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                bg: 'rgba(0, 0, 0, 0.5)',
                zIndex: 0,
            }}>
            <ModalLoading loading={loadingSubmission} />

            <Center minH='100vh' position='relative' zIndex={1} p={4} py={8}>
                <Box maxW='600px' w='full' bg={useColorModeValue('white', 'gray.800')} rounded='2xl' boxShadow='2xl' p={{ base: 6, md: 8 }} backdropFilter='blur(10px)'>
                    <Center mb={6} flexDirection='column' w='full'>
                        <Flex w='full' alignItems='center' justifyContent='center' flexDirection='column' gap={5} mb={2}>
                            <Image src={getStaticAsset('dream_us_logo.webp')} alt='Dream Us Logo' w='100px' />
                            <Text fontSize='xl' color='gray.500'>
                                X
                            </Text>
                            <Flex w='full' alignItems='center' justifyContent='center' gap={5}>
                                <Image src={getStaticAsset('[PPI] Logo.png')} w='80px' h='90%' fit='contain' mb={3} />
                                <Image src={getStaticAsset('[SPI] Logo_Purple.png')} w='80px' h='90%' fit='contain' mb={3} />
                                <Image src={getStaticAsset('[TSI] Logo.png')} w='80px' h='90%' fit='contain' mb={3} />
                            </Flex>
                        </Flex>
                        <Box textAlign='center'>
                            <Heading fontSize='2xl' color='primary.600' textAlign='center' mb={1}>
                                Create Your Visitor Account
                            </Heading>

                            <Text color='gray.600' fontSize='md' lineHeight={1.2} textAlign='center'>
                                Register your email and sign the waiver to continue.
                            </Text>
                            <Text color='gray.600' fontSize='md' lineHeight={1.2} textAlign='center' fontStyle='italic' fontWeight={600}>
                                Account registration is not a Membership.
                            </Text>
                        </Box>
                    </Center>
                    {/* Form */}
                    <form onSubmit={formik.handleSubmit}>
                        {step === 0 && (
                            <VStack spacing={4} align='stretch'>
                                {/* Full Name */}
                                <FormControl isRequired isInvalid={!!formik?.errors.fullname && formik?.touched.fullname}>
                                    <FormLabel htmlFor='fullname'>{t('full_name')}</FormLabel>
                                    <Input id='fullname' name='fullname' type='text' size='lg' placeholder='Enter your full name' onChange={formik.handleChange} value={formik?.values?.fullname ?? ''} />
                                    <FormErrorMessage>{formik?.errors.fullname}</FormErrorMessage>
                                </FormControl>

                                {/* Email */}
                                <FormControl isRequired isInvalid={!!formik?.errors.email && formik?.touched.email}>
                                    <FormLabel htmlFor='email'>{t('email')}</FormLabel>
                                    <Input id='email' name='email' type='email' size='lg' placeholder='Enter your email' onChange={formik.handleChange} value={formik?.values?.email ?? ''} />
                                    <FormErrorMessage>{formik?.errors.email}</FormErrorMessage>
                                </FormControl>

                                {/* Phone Number */}
                                <FormControl isRequired isInvalid={!!formik?.errors.phone && formik?.touched.phone}>
                                    <FormLabel htmlFor='phone'>{t('telp')}</FormLabel>
                                    <PhoneInput
                                        international
                                        autoComplete='off'
                                        style={{
                                            border: '1px solid',
                                            height: '3rem',
                                            borderRadius: '0.5rem',
                                            borderColor: '#e2e8f0',
                                            padding: '0 1rem',
                                            outline: 'none',
                                        }}
                                        id='phone'
                                        name='phone'
                                        defaultCountry='ID'
                                        placeholder='8123xxxxxx'
                                        hint='8123xxxxxx'
                                        value={formik?.values?.phone ?? ''}
                                        onChange={handleChangePhone}
                                    />
                                    <FormErrorMessage>{formik?.errors.phone}</FormErrorMessage>
                                </FormControl>

                                {/* Birth Date */}
                                <FormControl isRequired isInvalid={!!formik?.errors.birthDate && formik?.touched.birthDate}>
                                    <FormLabel htmlFor='birthDate'>{t('birth_date')}</FormLabel>
                                    <Input id='birthDate' name='birthDate' type='date' size='lg' onChange={formik.handleChange} value={formik?.values?.birthDate ?? ''} max={dateMax} />
                                    <FormErrorMessage>{formik?.errors.birthDate}</FormErrorMessage>
                                </FormControl>

                                {/* Gender */}
                                <FormControl isRequired isInvalid={!!formik?.errors.gender && formik?.touched.gender}>
                                    <FormLabel htmlFor='gender'>Gender</FormLabel>
                                    <RadioGroup id='gender' name='gender' onChange={(value) => formik.setFieldValue('gender', value)} value={formik?.values?.gender ?? ''}>
                                        <Stack direction='row' spacing={5}>
                                            <Radio value='male' size='lg' colorScheme='primary'>
                                                Male
                                            </Radio>
                                            <Radio value='female' size='lg' colorScheme='primary'>
                                                Female
                                            </Radio>
                                        </Stack>
                                    </RadioGroup>
                                    <FormErrorMessage>{formik?.errors.gender}</FormErrorMessage>
                                </FormControl>

                                {/* Action Buttons */}
                                {/* <HStack spacing={4} pt={4}>
                                    <Button w='full' size='lg' fontSize='md' variant='outline' colorScheme='gray' onClick={handleBack}>
                                        Cancel
                                    </Button>
                                    <Button w='full' size='lg' fontSize='md' type='button' colorScheme='primary' isDisabled={formik.values.fullname === '' || formik.values.phone === '' || formik.values.birthDate === ''} onClick={() => setStep(1)}>
                                        Continue
                                    </Button>
                                </HStack> */}

                                <HStack spacing={4} pt={4}>
                                    <Button w='full' size='lg' fontSize='md' variant='outline' colorScheme='gray' onClick={handleBack}>
                                        Back
                                    </Button>
                                    <Button w='full' size='lg' fontSize='md' type='submit' colorScheme='primary'>
                                        Next
                                    </Button>
                                </HStack>
                            </VStack>
                        )}

                        {/* {step === 1 && (
                            <VStack spacing={4} align='stretch'>
                                Gender
                                <FormControl isRequired isInvalid={!!formik?.errors.gender && formik?.touched.gender}>
                                    <FormLabel htmlFor='gender'>Gender</FormLabel>
                                    <RadioGroup id='gender' name='gender' onChange={(value) => formik.setFieldValue('gender', value)} value={formik?.values?.gender ?? ''}>
                                        <Stack direction='row' spacing={5}>
                                            <Radio value='male' size='lg' colorScheme='primary'>
                                                Male
                                            </Radio>
                                            <Radio value='female' size='lg' colorScheme='primary'>
                                                Female
                                            </Radio>
                                        </Stack>
                                    </RadioGroup>
                                    <FormErrorMessage>{formik?.errors.gender}</FormErrorMessage>
                                </FormControl>

                                Country
                                <FormControl isRequired isInvalid={!!formik?.errors.country && formik?.touched.country}>
                                    <FormLabel htmlFor='country'>{t('country')}</FormLabel>
                                    <Select
                                        id='country'
                                        name='country'
                                        options={options}
                                        defaultValue={{ value: 'ID', label: 'Indonesia' }}
                                        onChange={(v) => {
                                            formik.setFieldValue('country', v);
                                            formik.setFieldValue('city', { city: '9999' });
                                        }}
                                        value={formik?.values?.country ?? ''}
                                        size='lg'
                                    />
                                    <FormErrorMessage>{formik?.errors.country}</FormErrorMessage>
                                </FormControl>

                                City (Indonesia Only)
                                {formik?.values?.country?.value === 'ID' && (
                                    <FormControl isRequired={formik?.values?.country?.value === 'ID'} isInvalid={!!formik?.errors.city && formik?.touched.city}>
                                        <FormLabel htmlFor='city'>{t('city')}</FormLabel>
                                        <AsyncSelect cacheOptions id='city' name='city' onChange={(v) => formik.setFieldValue('city', v)} value={formik?.values?.city} loadOptions={promiseOptions} size='lg' />
                                        <FormErrorMessage>{formik?.errors.city}</FormErrorMessage>
                                    </FormControl>
                                )}

                                <HStack spacing={4} pt={4}>
                                    <Button w='full' size='lg' fontSize='md' variant='outline' colorScheme='gray' onClick={() => setStep(0)}>
                                        Back
                                    </Button>
                                    <Button w='full' size='lg' fontSize='md' type='submit' colorScheme='primary'>
                                        Regist Now!
                                    </Button>
                                </HStack>
                            </VStack>
                        )} */}
                    </form>
                </Box>
            </Center>
        </Box>
    );
}
